import { useTranslation } from 'next-i18next';

import { useState } from 'react';

import { CloseButton, Drawer, Portal, Select as ChakraSelect } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons/linear';

import { IndicatorButton } from '@app/components/atoms/indicator-button';
import { useSearchSorting } from '@app/hooks/useSearchSorting';

import { SortingDrawerProps } from './sorting-drawer.props';

export const SortingDrawer = ({ sortings, ...props }: SortingDrawerProps): JSX.Element => {
    const { t } = useTranslation('listing', { keyPrefix: 'sorting' });
    const [open, setOpen] = useState(false);
    const { collection, value, defaultValue, onValueChange } = useSearchSorting({ sortings });

    return (
        <Drawer.Root
            placement="bottom"
            variant={'autoHeight'}
            open={open}
            modal={false}
            lazyMount={true}
            onOpenChange={e => setTimeout(() => setOpen(e.open), 0)}
            {...props}
        >
            <Drawer.Trigger asChild>
                <IndicatorButton
                    data-testid={'sorting-drawer-trigger'}
                    withIndicator={value[0] !== 'default'}
                    icon={<IconsLinear.ArrangeVertical color={'secondary.solid'} w={4} h={4} />}
                    aria-label={t('titleMobile')}
                >
                    {t('titleMobile')}
                </IndicatorButton>
            </Drawer.Trigger>
            <Portal>
                <Drawer.Backdrop />
                <Drawer.Positioner>
                    <Drawer.Content mb={0} top={'auto'} bottom={0} data-testid={'sorting-drawer'}>
                        <Drawer.CloseTrigger asChild>
                            <CloseButton data-testid={'sorting-drawer-close'}>
                                <IconsLinear.Close />
                            </CloseButton>
                        </Drawer.CloseTrigger>
                        <Drawer.Header fontSize="md" borderBottomWidth={1} fontWeight={'bold'}>
                            {t('drawerTitle')}
                        </Drawer.Header>
                        <Drawer.Body p={0}>
                            <ChakraSelect.Root
                                open={true}
                                collection={collection}
                                value={value}
                                defaultValue={defaultValue}
                                variant={'clean'}
                                onValueChange={e => {
                                    onValueChange(e.value);
                                    setOpen(false);
                                }}
                                w={'full'}
                                size={'lg'}
                            >
                                <ChakraSelect.HiddenSelect />
                                <ChakraSelect.Content>
                                    {collection.items.map(item => (
                                        <ChakraSelect.Item
                                            item={item}
                                            key={item.value}
                                            pl={6}
                                            data-testid={'sorting-drawer-option'}
                                        >
                                            {item.label}
                                            <ChakraSelect.ItemIndicator />
                                        </ChakraSelect.Item>
                                    ))}
                                </ChakraSelect.Content>
                            </ChakraSelect.Root>
                        </Drawer.Body>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};
